import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import EpisodesPanel from "@/components/admin/EpisodesPanel";
import ReportPanel from "@/components/admin/ReportPanel";
import SongPanel from "@/components/admin/SongPanel";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import SubscribersPanel from "@/components/admin/SubscribersPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import BroadcastPanel from "@/components/admin/BroadcastPanel";
import AccountPanel from "@/components/admin/AccountPanel";

const TABS = [
  { id: "episodes", label: "Episodes" },
  { id: "report", label: "Shi Report" },
  { id: "song", label: "Song of the Week" },
  { id: "submissions", label: "Submissions" },
  { id: "subscribers", label: "Shi Sheet" },
  { id: "broadcast", label: "Send the Sheet" },
  { id: "premiere", label: "Premiere" },
  { id: "account", label: "Account" },
];

const PANELS = {
  episodes: EpisodesPanel,
  report: ReportPanel,
  song: SongPanel,
  submissions: SubmissionsPanel,
  subscribers: SubscribersPanel,
  broadcast: BroadcastPanel,
  premiere: SettingsPanel,
  account: AccountPanel,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("episodes");

  useEffect(() => {
    adminApi
      .me()
      .then(setUser)
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24">
        <p className="font-editorial italic text-2xl text-latte">Opening the booth…</p>
      </div>
    );
  }
  if (!user) return null;

  const Panel = PANELS[tab];

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-24 md:pt-32">
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
              The Booth — Studio CMS
            </p>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight text-cream md:text-5xl">
              Run the show, {user.name}.
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="inline-flex items-center gap-2 rounded-full border border-corduroy px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:border-gold hover:text-gold"
            >
              <ExternalLink size={15} /> View site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("twss_admin_token");
                navigate("/admin/login");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-wine/60 px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-wine"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-corduroy pb-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.id
                  ? "bg-ember text-ink"
                  : "border border-corduroy text-cream/80 hover:border-gold hover:text-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          <Panel user={user} />
        </div>
      </div>
    </div>
  );
}
